import React, { useRef, useState } from 'react';
import { Ci2HelpHexIcon } from './Ci2HelpHexIcon';
import { Ci2EditPopover, Ci2EditPopoverActions } from './Ci2EditPopover';
import {
  CAMPOS_GRUPO_KEYS,
  DefeitoRegBugDropdown,
  MOTIVO_DEFEITO_TOOLTIP,
  STOCK_VALIDATION_OPTIONS,
  normalizeStockValidationScope,
  sanitizeMotivoDraft,
  sanitizeAlnumCode,
  isExactAlnumCode,
} from './condicoesIntervShared';
import { Ci2ReservarCheck } from './Ci2ReservaCheck';

const STATUS_RESERVA_KEYS = ['status_disponivel', 'status_indisponivel'];

export function Ci2GrupoEditPopover({ open, anchorRef, row, api }) {
  const {
    CAMPOS_CONFIG,
    condicaoEditValues,
    updateCondicaoEditGrupo,
    setCondicaoEditValues,
    condicaoSaving,
    cancelEditCondicaoCampo,
    saveCondicaoCampo,
    canSaveGrupo,
    rowPodeMostrarReservaCheck,
  } = api;

  const isSaving = condicaoSaving === row.TB01055_CODIGO + '_grupo';
  const firstInputRef = useRef(null);
  const showReservaChecks = row.func_close === 1 && (canSaveGrupo() || rowPodeMostrarReservaCheck(row));
  const [stockHelpKey, setStockHelpKey] = useState(null);
  const stockScope = normalizeStockValidationScope(condicaoEditValues.stock_validation_scope);

  return (
    <Ci2EditPopover
      open={open}
      anchorRef={anchorRef}
      onClose={cancelEditCondicaoCampo}
      width={294}
      maxHeight={420}
      estimatedHeight={390}
      title="Editar operação e status"
      className="ci2-edit-popover--grupo"
      initialFocusRef={firstInputRef}
    >
      <div className="ci2-edit-popover-head ci2-edit-popover-head--shadcn">
        <h4 className="ci2-edit-popover-title">Operação e status</h4>
        <p className="ci2-edit-popover-sub">
          Defina os códigos de operação, condição e status para esta intervenção.
        </p>
      </div>
      <div className="ci2-edit-popover-body ci2-grupo-rows">
        {CAMPOS_GRUPO_KEYS.map((key) => {
          const cfg = CAMPOS_CONFIG.find((c) => c.key === key);
          const inputId = `ci2-grupo-${row.TB01055_CODIGO}-${key}`;
          return (
            <div
              key={key}
              className={`ci2-grupo-row${STATUS_RESERVA_KEYS.includes(key) ? ' ci2-grupo-row--status' : ''}`}
            >
              <label className="ci2-grupo-row-label" htmlFor={inputId}>
                {cfg?.label}
              </label>
              <div className="ci2-grupo-row-field">
                {showReservaChecks && STATUS_RESERVA_KEYS.includes(key) ? (
                  <div className="ci2-grupo-input-wrap">
                    <input
                      id={inputId}
                      type="text"
                      className="ci2-pop-input ci2-pop-input-mono ci2-grupo-row-input"
                      value={condicaoEditValues[key] ?? ''}
                      onChange={(e) => updateCondicaoEditGrupo(key, e.target.value)}
                      maxLength={cfg?.maxLen}
                      autoComplete="off"
                      spellCheck={false}
                    />
                    <Ci2ReservarCheck row={row} fieldKey={key} api={api} variant="in-input" />
                  </div>
                ) : (
                  <input
                    id={inputId}
                    ref={key === 'operacao' ? firstInputRef : undefined}
                    type="text"
                    className="ci2-pop-input ci2-pop-input-mono ci2-grupo-row-input"
                    value={condicaoEditValues[key] ?? ''}
                    onChange={(e) => updateCondicaoEditGrupo(key, e.target.value)}
                    maxLength={cfg?.maxLen}
                    autoComplete="off"
                    spellCheck={false}
                  />
                )}
              </div>
            </div>
          );
        })}

        <div className="ci2-grupo-stock-block">
          <div className="ci2-grupo-stock-sep" role="separator" />
          <h5 className="ci2-grupo-stock-title">Validação de estoque</h5>
          <div className="ci2-grupo-stock-options" role="radiogroup" aria-label="Validação de estoque">
            {STOCK_VALIDATION_OPTIONS.map((opt) => {
              const checked = stockScope === opt.value;
              const helpOpen = stockHelpKey === opt.value;
              return (
                <div key={opt.value} className="ci2-grupo-stock-option-wrap">
                  <div className={`ci2-grupo-stock-option-head${helpOpen ? ' is-help-open' : ''}`}>
                    <label className="dv2-sev-radio-item ci2-grupo-stock-option">
                      <input
                        type="radio"
                        className="dv2-sev-radio-input"
                        name={`ci2-stock-scope-${row.TB01055_CODIGO}`}
                        value={opt.value}
                        checked={checked}
                        disabled={isSaving}
                        onChange={() => {
                          setCondicaoEditValues((prev) => ({
                            ...prev,
                            stock_validation_scope: opt.value,
                          }));
                        }}
                      />
                      <span className="dv2-sev-radio-mark" aria-hidden />
                      <span className="dv2-sev-radio-text">{opt.label}</span>
                    </label>
                    <Ci2HelpHexIcon
                      className="ci2-grupo-stock-help"
                      label={`Ajuda: ${opt.label}`}
                      expanded={helpOpen}
                      onToggle={() => setStockHelpKey((v) => (v === opt.value ? null : opt.value))}
                    />
                    {helpOpen ? (
                      <div className="ci2-grupo-stock-tip" role="tooltip">
                        {opt.tip}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Ci2EditPopoverActions
        onCancel={cancelEditCondicaoCampo}
        onConfirm={() => saveCondicaoCampo(row)}
        confirmDisabled={!canSaveGrupo()}
        saving={isSaving}
        confirmTitle={
          canSaveGrupo()
            ? 'Salvar os 4 campos'
            : 'Operação e status: 2 caracteres (A-Z/0-9); Condição: 3 caracteres'
        }
      />
    </Ci2EditPopover>
  );
}

const SINGLE_FIELD_SUBS = {
  status_retorno: 'Defina o código de status aplicado ao retorno técnico.',
  status_peca_disp:
    'Status do pedido de peça no momento em que estiver liberado (processo de saída de estoque).',
};

export function Ci2SingleFieldEditPopover({ open, anchorRef, row, fieldKey, label, maxLen, api }) {
  const {
    condicaoEditValue,
    setCondicaoEditValue,
    condicaoSaving,
    cancelEditCondicaoCampo,
    saveCondicaoCampo,
  } = api;

  const isSaving = condicaoSaving === row.TB01055_CODIGO + '_' + fieldKey;
  const inputRef = useRef(null);
  const isRetorno = fieldKey === 'status_retorno';
  const canSave = (() => {
    const v = (condicaoEditValue ?? '').trim();
    if (isRetorno) return v.length === 0 || isExactAlnumCode(v, 2);
    if (fieldKey === 'status_peca_disp') return v.length === 0 || v.length === 2;
    return true;
  })();

  const inputId = `ci2-campo-${row.TB01055_CODIGO}-${fieldKey}`;
  const subtitle = SINGLE_FIELD_SUBS[fieldKey];

  return (
    <Ci2EditPopover
      open={open}
      anchorRef={anchorRef}
      onClose={cancelEditCondicaoCampo}
      width={250}
      maxHeight={290}
      estimatedHeight={170}
      title={label}
      className="ci2-edit-popover--grupo ci2-edit-popover--single"
      initialFocusRef={inputRef}
    >
      <div className="ci2-edit-popover-head ci2-edit-popover-head--shadcn">
        <h4 className="ci2-edit-popover-title">{label}</h4>
        {subtitle ? <p className="ci2-edit-popover-sub">{subtitle}</p> : null}
      </div>
      <div className="ci2-edit-popover-body ci2-grupo-rows">
        <div className="ci2-grupo-row">
          <label className="ci2-grupo-row-label" htmlFor={inputId}>
            Código
          </label>
          <div className="ci2-grupo-row-field">
            <input
              id={inputId}
              ref={inputRef}
              type="text"
              className="ci2-pop-input ci2-pop-input-mono ci2-grupo-row-input"
              value={condicaoEditValue}
              onChange={(e) =>
                setCondicaoEditValue(
                  isRetorno
                    ? sanitizeAlnumCode(e.target.value, maxLen)
                    : e.target.value.slice(0, maxLen).toUpperCase()
                )
              }
              maxLength={maxLen}
              autoComplete="off"
              spellCheck={false}
              placeholder="Ex: 01"
            />
          </div>
        </div>
      </div>
      <Ci2EditPopoverActions
        onCancel={cancelEditCondicaoCampo}
        onConfirm={() => saveCondicaoCampo(row)}
        confirmDisabled={!canSave}
        saving={isSaving}
        confirmTitle={
          canSave
            ? 'Salvar alteração'
            : isRetorno
              ? 'Informe 2 caracteres (A-Z/0-9)'
              : 'Informe um código válido'
        }
      />
    </Ci2EditPopover>
  );
}

export function Ci2DefeitoEditPopover({ open, anchorRef, row, api }) {
  const {
    defeitoDraftCodbug,
    setDefeitoDraftCodbug,
    defeitoDraftReason,
    setDefeitoDraftReason,
    defeitoDraftBlank,
    setDefeitoDraftBlank,
    defeitoDraftAsk,
    setDefeitoDraftAsk,
    nativeMode,
    regBugsList,
    regBugsLoading,
    defeitoSavingId,
    defeitoDdOpenCod,
    setDefeitoDdOpenCod,
    defeitoMotivoHelpOpen,
    setDefeitoMotivoHelpOpen,
    saveDefeitoVinculo,
    closeDefeitoPainel,
  } = api;

  const isSaving = defeitoSavingId === row.TB01055_CODIGO;

  return (
    <Ci2EditPopover
      open={open}
      anchorRef={anchorRef}
      onClose={closeDefeitoPainel}
      width={294}
      maxHeight={420}
      estimatedHeight={380}
      className="ci2-edit-popover--grupo ci2-edit-popover--defeito"
      title={nativeMode ? 'Configurar serviço' : 'Configurar defeito'}
    >
      <div className="ci2-edit-popover-head ci2-edit-popover-head--shadcn">
        <h4 className="ci2-edit-popover-title">{nativeMode ? 'Serviços em retorno' : 'Defeito em retorno'}</h4>
        <p className="ci2-edit-popover-sub">{nativeMode ? 'Vincule serviço e motivo padrão na nova OS' : 'Vincule defeito e motivo padrão na nova OS'}</p>
      </div>
      <div className="ci2-edit-popover-body ci2-grupo-rows ci2-defeito-pop-body">
        <div className="ci2-grupo-row">
          <label className="ci2-grupo-row-label" htmlFor={`ci2-defeito-${row.TB01055_CODIGO}`}>
            {nativeMode ? 'Serviços' : 'Defeito'}
          </label>
          <div className="ci2-grupo-row-field">
            <DefeitoRegBugDropdown
              variant="ci2"
              buttonId={`ci2-defeito-${row.TB01055_CODIGO}`}
              value={defeitoDraftCodbug}
              regBugsList={regBugsList}
              disabled={isSaving || regBugsLoading}
              rowCod={row.TB01055_CODIGO}
              openCod={defeitoDdOpenCod}
              setOpenCod={setDefeitoDdOpenCod}
              onChange={setDefeitoDraftCodbug}
            />
            {regBugsLoading ? <span className="condicoes-defeito-loading">Carregando…</span> : null}
          </div>
        </div>

        <div className="ci2-grupo-row ci2-grupo-row--motivo">
          <div className={`ci2-motivo-head${defeitoMotivoHelpOpen ? ' ci2-motivo-head--open' : ''}`}>
            <label className="ci2-grupo-row-label" htmlFor={`ci2-motivo-${row.TB01055_CODIGO}`}>
              Motivo
            </label>
            <Ci2HelpHexIcon
              className="condicoes-defeito-motivo-info"
              label="Ajuda sobre Motivo"
              expanded={defeitoMotivoHelpOpen}
              onToggle={() => setDefeitoMotivoHelpOpen((v) => !v)}
            />
            {defeitoMotivoHelpOpen ? (
              <div className="ci2-motivo-help" role="tooltip">
                {MOTIVO_DEFEITO_TOOLTIP}
              </div>
            ) : null}
          </div>
          <div className="ci2-grupo-row-field">
            <input
              id={`ci2-motivo-${row.TB01055_CODIGO}`}
              type="text"
              className="ci2-pop-input ci2-grupo-row-input"
              value={defeitoDraftReason}
              onChange={(e) => setDefeitoDraftReason(sanitizeMotivoDraft(e.target.value))}
              maxLength={50}
              autoComplete="off"
              spellCheck={false}
              placeholder="Opcional"
              disabled={isSaving}
            />
          </div>
        </div>

        <label className="ci2-reserva-check-label ci2-defeito-blank-check">
          <input
            type="checkbox"
            className="ci2-reserva-check-native"
            checked={defeitoDraftBlank}
            disabled={isSaving}
            onChange={(e) => setDefeitoDraftBlank(e.target.checked)}
          />
          <span className="ci2-reserva-check-box" aria-hidden />
          <span className="ci2-reserva-check-text">{nativeMode ? 'Abrir sem serviço' : 'Abrir sem defeito'}</span>
        </label>
        {nativeMode ? (
          <label className="ci2-reserva-check-label ci2-defeito-blank-check ci2-defeito-ask-check">
            <input
              type="checkbox"
              className="ci2-reserva-check-native"
              checked={!!defeitoDraftAsk}
              disabled={isSaving}
              onChange={(e) => setDefeitoDraftAsk(e.target.checked)}
            />
            <span className="ci2-reserva-check-box" aria-hidden />
            <span className="ci2-reserva-check-text">{nativeMode ? 'Perguntar novo serviço em novo ciclo' : 'Perguntar novo defeito em novo ciclo'}</span>
          </label>
        ) : null}
      </div>
      <Ci2EditPopoverActions
        onCancel={closeDefeitoPainel}
        onConfirm={() => saveDefeitoVinculo(row)}
        saving={isSaving}
      />
    </Ci2EditPopover>
  );
}
